import { Spacing } from '@persona-id/design-tokens';
import { CircleCheckIcon } from '@persona-id/design-tokens/icons';
import dompurify from 'dompurify';
import { create, Delta, formatters } from 'jsondiffpatch';
import { isEmpty } from 'lodash/fp';
import { useMemo } from 'react';
import styled from 'styled-components';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { BlockInput, BlockOutput } from 'dashboard/models/workflow-block';
import { WorkflowStep } from 'dashboard/models/workflow-version';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Heading, Paragraph, Span } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { Variant } from 'design-system/theme';
import { BASE } from 'design-system/variables/spacing';
import useToggle from 'lib/hooks/useToggle';

const FOOTER_HEIGHT = 56;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

const NoContentContainer = styled(FlexContainer)`
  justify-content: center;
  width: 100%;
  height: 100%;
`;
const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
  position: relative;
  flex-direction: column;
  flex-grow: 1;
  flex: auto;
  overflow-y: auto;
  padding: ${Spacing[5]};
  padding-bottom: ${FOOTER_HEIGHT + BASE * 6}px;
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: inherit;
`;

const DiffContainer = styled.div`
  padding: ${Spacing[2]};
  margin-top: ${Spacing[3]};
  margin-bottom: ${Spacing[3]};
  background-color: ${({ theme }) => theme.color.background.secondary};
  max-height: 600px;
  overflow-y: auto;
  // default highlight styling provided by jsondiffpatch
  .jsondiffpatch-delta {
    font-family: 'Bitstream Vera Sans Mono', 'DejaVu Sans Mono', Monaco, Courier, monospace;
    font-size: 12px;
    margin: 0;
    padding: 0 0 0 12px;
    display: inline-block;
  }
  .jsondiffpatch-delta pre {
    font-family: 'Bitstream Vera Sans Mono', 'DejaVu Sans Mono', Monaco, Courier, monospace;
    font-size: 12px;
    margin: 0;
    padding: 0;
    display: inline-block;
  }
  ul.jsondiffpatch-delta {
    list-style-type: none;
    padding: 0 0 0 20px;
    margin: 0;
  }
  .jsondiffpatch-delta ul {
    list-style-type: none;
    padding: 0 0 0 20px;
    margin: 0;
  }
  .jsondiffpatch-added .jsondiffpatch-property-name,
  .jsondiffpatch-added .jsondiffpatch-value pre,
  .jsondiffpatch-modified .jsondiffpatch-right-value pre,
  .jsondiffpatch-textdiff-added {
    background: #bbffbb;
  }
  .jsondiffpatch-deleted .jsondiffpatch-property-name,
  .jsondiffpatch-deleted pre,
  .jsondiffpatch-modified .jsondiffpatch-left-value pre,
  .jsondiffpatch-textdiff-deleted {
    background: #ffbbbb;
    text-decoration: line-through;
  }
  .jsondiffpatch-unchanged,
  .jsondiffpatch-movedestination {
    color: gray;
  }
  .jsondiffpatch-unchanged,
  .jsondiffpatch-movedestination > .jsondiffpatch-value {
    transition: all 0.5s;
    -webkit-transition: all 0.5s;
    overflow-y: hidden;
  }
  .jsondiffpatch-unchanged-showing .jsondiffpatch-unchanged,
  .jsondiffpatch-unchanged-showing .jsondiffpatch-movedestination > .jsondiffpatch-value {
    max-height: 100px;
  }
  .jsondiffpatch-unchanged-hidden .jsondiffpatch-unchanged,
  .jsondiffpatch-unchanged-hidden .jsondiffpatch-movedestination > .jsondiffpatch-value {
    max-height: 0;
  }
  .jsondiffpatch-unchanged-hiding .jsondiffpatch-movedestination > .jsondiffpatch-value,
  .jsondiffpatch-unchanged-hidden .jsondiffpatch-movedestination > .jsondiffpatch-value {
    display: block;
  }
  .jsondiffpatch-unchanged-visible .jsondiffpatch-unchanged,
  .jsondiffpatch-unchanged-visible .jsondiffpatch-movedestination > .jsondiffpatch-value {
    max-height: 100px;
  }
  .jsondiffpatch-unchanged-hiding .jsondiffpatch-unchanged,
  .jsondiffpatch-unchanged-hiding .jsondiffpatch-movedestination > .jsondiffpatch-value {
    max-height: 0;
  }
  .jsondiffpatch-unchanged-showing .jsondiffpatch-arrow,
  .jsondiffpatch-unchanged-hiding .jsondiffpatch-arrow {
    display: none;
  }
  .jsondiffpatch-value {
    display: inline-block;
  }
  .jsondiffpatch-property-name {
    display: inline-block;
    padding-right: 5px;
    vertical-align: top;
  }
  .jsondiffpatch-property-name:after {
    content: ': ';
  }
  .jsondiffpatch-child-node-type-array > .jsondiffpatch-property-name:after {
    content: ': [';
  }
  .jsondiffpatch-child-node-type-array:after {
    content: '],';
  }
  div.jsondiffpatch-child-node-type-array:before {
    content: '[';
  }
  div.jsondiffpatch-child-node-type-array:after {
    content: ']';
  }
  .jsondiffpatch-child-node-type-object > .jsondiffpatch-property-name:after {
    content: ': {';
  }
  .jsondiffpatch-child-node-type-object:after {
    content: '},';
  }
  div.jsondiffpatch-child-node-type-object:before {
    content: '{';
  }
  div.jsondiffpatch-child-node-type-object:after {
    content: '}';
  }
  .jsondiffpatch-value pre:after {
    content: ',';
  }
  li:last-child > .jsondiffpatch-value pre:after,
  .jsondiffpatch-modified > .jsondiffpatch-left-value pre:after {
    content: '';
  }
  .jsondiffpatch-modified .jsondiffpatch-value {
    display: inline-block;
  }
  .jsondiffpatch-modified .jsondiffpatch-right-value {
    margin-left: 5px;
  }
  .jsondiffpatch-moved .jsondiffpatch-value {
    display: none;
  }
  .jsondiffpatch-moved .jsondiffpatch-moved-destination {
    display: inline-block;
    background: #ffffbb;
    color: #888;
  }
  .jsondiffpatch-moved .jsondiffpatch-moved-destination:before {
    content: ' => ';
  }
  ul.jsondiffpatch-textdiff {
    padding: 0;
  }
  .jsondiffpatch-textdiff-location {
    color: #bbb;
    display: inline-block;
    min-width: 60px;
  }
  .jsondiffpatch-textdiff-line {
    display: inline-block;
  }
  .jsondiffpatch-textdiff-line-number:after {
    content: ',';
  }
  .jsondiffpatch-error {
    background: red;
    color: white;
    font-weight: bold;
  }
`;

const ToggleWrapper = styled(FlexContainer)`
  flex-direction: row;
`;

const ToggleLabel = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  color: ${({ theme }) => theme.color.text.tertiary};
  margin-left: ${Spacing[2]};
`;

const StyledCheckIcon = styled(CircleCheckIcon)`
  color: ${({ theme }) => theme.color.variants.success.default};
  margin-right: ${Spacing[2]};
`;

type DiffObject =
  | { 'steps-config': WorkflowStep[] }
  | { inputs: BlockInput[] }
  | { outputs: BlockOutput[] };
export type DiffConfig = {
  comparedObject: DiffObject;
  currentObject: DiffObject;
  description: string;
};

interface WorkflowsJsonVersionDiffProps {
  diffConfigs: DiffConfig[];
  baseVersionId?: string;
  headerText?: string;
  targetVersionId: string;
  loading: boolean;
}

const WorkflowsJsonVersionDiff = ({
  diffConfigs,
  baseVersionId,
  headerText = 'Workflow',
  targetVersionId,
  loading,
}: WorkflowsJsonVersionDiffProps) => {
  const workflowsDiffContent = useMemo(() => {
    if (loading || !diffConfigs.length) {
      return (
        <NoContentContainer>
          <LoadingIndicator />
        </NoContentContainer>
      );
    }

    const workflowDiffPatcher = create({
      objectHash: function (obj: { token: string }, index: number) {
        // matching by name avoids visualizing a deleted step as an off-by-one diff for subsequent steps
        return obj.token || '$$index:' + index;
      },
      array: {
        detectMove: false,
      },
    });
    return (
      <>
        <Heading size='sm'>
          {headerText} ({targetVersionId ?? baseVersionId})
        </Heading>
        <Spacer height={1} />
        {diffConfigs.map(({ comparedObject, description, currentObject }, index) => (
          <DiffViewCard
            key={index}
            description={description}
            status={isEmpty(comparedObject) ? 'success' : isEmpty(currentObject) ? 'danger' : null}
            diff={workflowDiffPatcher.diff(comparedObject, currentObject)}
            baseObject={comparedObject}
          />
        ))}
      </>
    );
  }, [loading, diffConfigs, baseVersionId, targetVersionId, headerText]);

  return <Container>{workflowsDiffContent}</Container>;
};

type DiffObjStatus = Extract<Variant, 'success' | 'danger'> | null;

interface DiffViewCardProps {
  description?: string;
  status?: DiffObjStatus;
  diff: Delta | undefined;
  baseObject: DiffObject;
}

const DiffViewCard = ({ description, status, diff, baseObject }: DiffViewCardProps) => {
  const [showUnchanged, { toggle: toggleShowUnchanged }] = useToggle(false);
  const toggleLabel = showUnchanged ? 'Hide unchanged' : 'Show unchanged';
  const diffHTML =
    diff != null ? formatters.html.format(diff, showUnchanged ? baseObject : {}) : null;
  return (
    <Card>
      <Card.Body>
        <FlexContainer>
          <Span>{description}</Span>
          <Spacer width={1} height={0} />
          {status != null && (
            <Sticker variant={status}>{status === 'success' ? 'New' : 'Deleted'}</Sticker>
          )}
        </FlexContainer>
        {diff != null ? (
          <>
            <ToggleWrapper>
              <ToggleSlider
                aria-label={toggleLabel}
                value={showUnchanged}
                onChange={toggleShowUnchanged}
              />
              <ToggleLabel>{toggleLabel}</ToggleLabel>
            </ToggleWrapper>
            <DiffContainer dangerouslySetInnerHTML={{ __html: dompurify.sanitize(diffHTML) }} />
          </>
        ) : (
          // Should never render this message since the modal can only be opened if there are changes
          <FlexContainer>
            <StyledCheckIcon size={24} />
            <Paragraph size='md'>No changes</Paragraph>
          </FlexContainer>
        )}
      </Card.Body>
    </Card>
  );
};

export default WorkflowsJsonVersionDiff;
